(() => {
  if (customElements.get('safe-walk-addons')) return;
  class SafeWalkAddons extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.root = this.closest('[data-swv5-section]');
      this.form = document.getElementById(this.dataset.productFormId);
      this.cards = [...this.querySelectorAll('[data-addon-card]')];
      this.dialog = this.querySelector('[data-addon-dialog]');
      this.error = this.querySelector('[data-addon-error]');
      this.data = {
        main: this.readJson('[data-addon-variants="main"]'), bungee: this.readJson('[data-addon-variants="bungee"]'), collar: this.readJson('[data-addon-variants="collar"]'),
        mainOptions: this.readJson('[data-addon-options="main"]'), bungeeOptions: this.readJson('[data-addon-options="bungee"]'), collarOptions: this.readJson('[data-addon-options="collar"]')
      };
      this.bound = {
        change: (event) => this.onChange(event), click: (event) => this.onClick(event), variant: () => queueMicrotask(() => this.update()),
        submit: (event) => this.onSubmit(event), dialogClose: () => this.infoTrigger?.focus(), mutation: () => this.scheduleBridge()
      };
      this.addEventListener('change', this.bound.change);
      this.addEventListener('click', this.bound.click);
      this.root?.addEventListener('swv5:variant-change', this.bound.variant);
      this.root?.addEventListener('swv5:purchase-ready', this.bound.variant);
      this.form?.addEventListener('submit', this.bound.submit, true);
      this.dialog?.addEventListener('close', this.bound.dialogClose);
      this.bridgeObserver = new MutationObserver(this.bound.mutation);
      this.bridgeObserver.observe(document.documentElement, { childList: true, subtree: true });
      this.update();
    }
    disconnectedCallback() {
      this.removeEventListener('change', this.bound?.change);
      this.removeEventListener('click', this.bound?.click);
      this.root?.removeEventListener('swv5:variant-change', this.bound?.variant);
      this.root?.removeEventListener('swv5:purchase-ready', this.bound?.variant);
      this.form?.removeEventListener('submit', this.bound?.submit, true);
      this.dialog?.removeEventListener('close', this.bound?.dialogClose);
      this.bridgeObserver?.disconnect();
      if (this.bridgeFrame) cancelAnimationFrame(this.bridgeFrame);
      this.initialized = false;
    }
    readJson(selector) {
      try { return JSON.parse(this.querySelector(selector)?.textContent || '[]'); }
      catch { return []; }
    }
    normalize(value) { return String(value || '').trim().toLowerCase(); } normalizeSize(value) { return window.SafeWalkV51AccessoryVariants?.normalizeSize?.(value) || this.normalize(value); }
    optionIndex(options, name) { return options.findIndex((option) => this.normalize(option) === this.normalize(name)); }
    currentMain() {
      const id = this.form?.querySelector('[name="id"]')?.value;
      return this.data.main.find((variant) => String(variant.id) === String(id)) || null;
    }
    matchingVariant(kind, color, size) {
      const variants = this.data[kind];
      const options = this.data[`${kind}Options`];
      const colorIndex = this.optionIndex(options, this.dataset.colorOptionName);
      const sizeIndex = this.optionIndex(options, this.dataset.sizeOptionName);
      if (colorIndex < 0 || (kind === 'collar' && sizeIndex < 0)) return null;
      return variants.find((variant) => {
        const colorMatches = this.normalize(variant.options?.[colorIndex]) === this.normalize(color);
        const sizeMatches = kind !== 'collar' || this.normalizeSize(variant.options?.[sizeIndex]) === this.normalizeSize(size);
        return colorMatches && sizeMatches;
      }) || null;
    }
    input(kind) { return this.querySelector(`[data-addon-card="${kind}"] [data-addon-input]`); }
    checked(kind) { const input = this.input(kind); return Boolean(input?.checked && !input.disabled); }
    packageName(state) {
      if (state.includesBungee && state.includesCollar) return 'full_set';
      if (state.includesBungee) return 'harness_bungee';
      if (state.includesCollar) return 'harness_collar';
      return 'harness_only';
    }
    selection() {
      const main = this.currentMain();
      if (!main) return { available: false, main: null };
      const colorIndex = this.optionIndex(this.data.mainOptions, this.dataset.colorOptionName);
      const sizeIndex = this.optionIndex(this.data.mainOptions, this.dataset.sizeOptionName);
      if (colorIndex < 0 || sizeIndex < 0) return { available: false, main };
      const color = main.options?.[colorIndex] || '';
      const size = main.options?.[sizeIndex] || '';
      const bungee = this.matchingVariant('bungee', color, size);
      const collar = this.matchingVariant('collar', color, size);
      const includesBungee = this.checked('bungee');
      const includesCollar = this.checked('collar');
      const required = [main];
      if (includesBungee) required.push(bungee);
      if (includesCollar) required.push(collar);
      let price = Number(main.price || 0);
      if (includesBungee) price += Number(this.dataset.bungeePrice || 0);
      if (includesCollar) price += Number(this.dataset.collarPrice || 0);
      return { main, bungee, collar, color, size, includesBungee, includesCollar, price,
        available: this.dataset.giftAvailable === 'true' && required.every((variant) => variant?.available !== false) };
    }
    money(cents) {
      const currency = this.root?.dataset.currency || window.Shopify?.currency?.active || 'ILS';
      return new Intl.NumberFormat(document.documentElement.lang || 'he-IL', {
        style: 'currency', currency, minimumFractionDigits: Number(cents) % 100 ? 2 : 0, maximumFractionDigits: 2
      }).format(Number(cents || 0) / 100).replace(/\s/g, ' ');
    }
    variantImage(variant, width) {
      const source = variant?.featured_image?.src || variant?.featured_image?.url || '';
      if (!source) return '';
      try {
        const url = new URL(source, location.origin);
        url.searchParams.set('width', String(width));
        return url.href;
      } catch { return source; }
    }
    updateCard(kind, variant, state) {
      const card = this.querySelector(`[data-addon-card="${kind}"]`);
      if (!card) return;
      const input = card.querySelector('[data-addon-input]');
      const available = Boolean(variant?.available);
      if (!available && input) input.checked = false;
      if (input) input.disabled = !available;
      card.classList.toggle('is-disabled', !available);
      card.querySelector('[data-addon-status]')?.toggleAttribute('hidden', available);
      const match = card.querySelector('[data-addon-match]');
      if (match) match.textContent = kind === 'collar' ? `${state.color} · ${this.normalizeSize(state.size)}` : state.color;
      const standalone = card.querySelector('[data-addon-standalone]');
      if (standalone) standalone.textContent = variant ? this.money(variant.price) : '';
      const price = card.querySelector('[data-addon-price]');
      if (price) price.textContent = this.money(this.dataset[`${kind}Price`]);
      const image = card.querySelector('img');
      const source = this.variantImage(variant, 144);
      if (image && source && image.src !== source) image.src = source;
      if (image) image.dataset.zoomSrc = this.variantImage(variant, 900) || source;
    }
    explicitSize(state) { return Boolean(state.size && this.normalize(state.size) === this.normalize(this.root?.dataset.sizeConfirmedValue)); }
    stickyActions() { const formId = this.dataset.productFormId; return formId ? [...new Set([...document.querySelectorAll('[data-swv51-sticky-actions] [data-swv5-submit]')].filter((button) => button.getAttribute('form') === formId).map((button) => button.closest('[data-swv51-sticky-actions]')).filter(Boolean))] : []; }
    buttonLabel(label, disabled) {
      const primary = this.form?.querySelector('[data-swv5-submit]'), buttons = [...new Set([primary, ...(this.root?.querySelectorAll('[data-swv5-submit]') || []), ...this.stickyActions().flatMap((actions) => [...actions.querySelectorAll('[data-swv5-submit]')])].filter(Boolean))];
      buttons.forEach((button) => {
        button.disabled = disabled || this.busy;
        button.setAttribute('aria-disabled', String(disabled || this.busy));
        const node = button.querySelector('[data-atc-label],[data-sticky-atc-label]');
        if (node) node.textContent = label;
      });
    }
    syncSurfaces(state) {
      const price = this.money(state.price);
      const priceNodes = [...new Set([...(this.root?.querySelectorAll('[data-atc-price],[data-mobile-price],[data-desktop-price]') || []), ...this.stickyActions().flatMap((actions) => [...actions.querySelectorAll('[data-mobile-price],[data-desktop-price]')])])]; priceNodes.forEach((node) => { node.textContent = price; });
      const ready = state.available && this.bridgeActive;
      const label = !this.explicitSize(state)
        ? this.dataset.sizeRequiredLabel
        : ready ? this.dataset.buttonLabel
          : state.available ? this.dataset.discountLoadingLabel : this.dataset.buttonUnavailableLabel;
      this.buttonLabel(label, !ready || !this.explicitSize(state));
    }
    update() {
      const initial = this.selection();
      if (!initial.main) return;
      this.updateCard('bungee', initial.bungee, initial);
      this.updateCard('collar', initial.collar, initial);
      const state = this.selection();
      this.state = state;
      this.syncSurfaces(state);
      this.emitState(state);
      this.scheduleBridge();
    }
    emitState(state, changedAddon = '') {
      const detail = {
        kind: this.packageName(state), addon: changedAddon, checked: changedAddon ? this.checked(changedAddon) : undefined, totalCents: state.price,
        harnessVariantId: state.main?.id || null, bungeeVariantId: state.includesBungee ? state.bungee?.id || null : null,
        collarVariantId: state.includesCollar ? state.collar?.id || null : null };
      this.dispatchEvent(new CustomEvent('swv5:addon-change', { bubbles: true, detail }));
      this.dispatchEvent(new CustomEvent('swv5:package-change', { bubbles: true, detail }));
    }
    onChange(event) {
      const input = event.target.closest('[data-addon-input]');
      if (!input) return;
      this.hideError();
      const kind = input.closest('[data-addon-card]')?.dataset.addonCard || '';
      this.update();
      this.emitState(this.state, kind);
      window.clarity?.('event', `safewalk_addon_${kind}_${input.checked ? 'on' : 'off'}`);
    }
    onClick(event) {
      const info = event.target.closest('[data-addon-info]');
      if (info) {
        event.preventDefault();
        this.openInfo(info.dataset.addonInfo, info);
        return;
      }
      if (event.target.closest('[data-addon-dialog-close]') || event.target === this.dialog) this.dialog?.close();
    }
    openInfo(kind, trigger) {
      const state = this.selection();
      const variant = state[kind];
      const card = this.querySelector(`[data-addon-card="${kind}"]`);
      if (!variant || !card || !this.dialog) return;
      this.infoTrigger = trigger;
      const image = this.dialog.querySelector('[data-addon-dialog-image]');
      if (image) {
        image.src = card.querySelector('img')?.dataset.zoomSrc || this.variantImage(variant, 900);
        image.alt = card.querySelector('img')?.alt || '';
      }
      this.dialog.querySelector('[data-addon-dialog-title]').textContent = card.querySelector('.swv51-addon__title-line strong')?.textContent || '';
      this.dialog.querySelector('[data-addon-dialog-match]').textContent = card.querySelector('[data-addon-match]')?.textContent || '';
      this.dialog.querySelector('[data-addon-dialog-standalone]').textContent = this.money(variant.price);
      this.dialog.querySelector('[data-addon-dialog-price]').textContent = this.money(this.dataset[`${kind}Price`]);
      const template = this.querySelector(`[data-addon-info-template="${kind}"]`), body = this.dialog.querySelector('[data-addon-dialog-body]');
      if (body && template) body.replaceChildren(template.content.cloneNode(true));
      this.querySelectorAll(`[data-addon-benefit-for="${kind}"]`).forEach((source) => { const item = document.createElement('li'); item.textContent = source.textContent?.trim() || ''; body?.querySelector('[data-addon-dialog-benefits]')?.append(item); });
      if (!this.dialog.open) this.dialog.showModal();
    }
    scheduleBridge() {
      if (this.bridgeFrame) return;
      this.bridgeFrame = requestAnimationFrame(() => {
        this.bridgeFrame = 0;
        this.bridgeActive = this.syncKaching(this.selection());
        this.syncSurfaces(this.selection());
      });
    }
    kachingRow(bundle, kind) {
      const handle = this.dataset[`${kind}Handle`];
      return [...bundle?.querySelectorAll('[data-upsell-id]') || []].find((row) =>
        [...row.querySelectorAll('a[href*="/products/"]')].some((anchor) => anchor.href.includes(`/products/${handle}`))) || null;
    }
    setKachingOption(scope, name, value) {
      const select = [...scope?.querySelectorAll('select') || []].find((field) =>
        this.normalize(field.getAttribute('aria-label')) === this.normalize(name));
      const normalizer = this.normalize(name) === this.normalize(this.dataset.sizeOptionName) ? 'normalizeSize' : 'normalize', option = [...select?.options || []].find((entry) => this[normalizer](entry.value) === this[normalizer](value));
      if (!select || !option || select.value === option.value) return Boolean(select && option);
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    syncKaching(state) {
      const bundle = this.form?.querySelector(`kaching-bundle[product-id="${this.dataset.mainProductId}"]`)?.querySelector('kaching-bundles-block[deal-block]');
      const base = [...bundle?.querySelectorAll('input[name^="kaching-bundles-deal"]') || []]
        .find((input) => input.value === this.dataset.kachingBaseBarId);
      if (!bundle || !base || typeof bundle.items !== 'function') return false;
      if (!base.checked) base.click();
      const bar = base.closest('.kaching-bundles__bar') || bundle;
      this.setKachingOption(bar, this.dataset.colorOptionName, state.color);
      this.setKachingOption(bar, this.dataset.sizeOptionName, state.size);
      let ready = Boolean(base.checked);
      for (const kind of ['bungee', 'collar']) {
        const row = this.kachingRow(bundle, kind);
        const selected = kind === 'bungee' ? state.includesBungee : state.includesCollar;
        if (!row) { if (selected) ready = false; continue; }
        if (selected) {
          ready = this.setKachingOption(row, this.dataset.colorOptionName, state.color) && ready;
          if (kind === 'collar') ready = this.setKachingOption(row, this.dataset.sizeOptionName, state.size) && ready;
        }
        const toggle = row.querySelector('.kaching-bundles__upsell__checkbox');
        if (!toggle) { if (selected) ready = false; continue; }
        const active = toggle.classList.contains('kaching-bundles__upsell__checkbox--selected');
        if (active !== selected) toggle.click();
      }
      this.kachingBundle = bundle;
      return ready;
    }
    async waitForBridge(timeout = 8000) {
      const deadline = Date.now() + timeout;
      while (Date.now() < deadline) {
        const state = this.selection();
        this.bridgeActive = this.syncKaching(state);
        if (this.bridgeActive) return state;
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      }
      return null;
    }
    metadata(item) {
      const raw = item?.properties?.__kaching_bundles;
      if (raw && typeof raw === 'object') return raw;
      try { return JSON.parse(String(raw || '')); }
      catch { try { return JSON.parse(atob(String(raw || ''))); } catch { return null; } }
    }
    validatedItems(state) {
      const items = this.kachingBundle?.items?.() || [];
      const expected = [state.main.id];
      if (state.includesBungee) expected.push(state.bungee.id);
      if (state.includesCollar) expected.push(state.collar.id);
      expected.push(Number(this.dataset.giftVariantId));
      if (items.length !== expected.length || expected.some((id) => !items.some((item) => Number(item.id) === Number(id)))) return null;
      if (items.some((item) => Number(item.quantity || 1) !== 1 || !this.metadata(item))) return null;
      const main = items.find((item) => Number(item.id) === Number(state.main.id));
      const gift = items.find((item) => Number(item.id) === Number(this.dataset.giftVariantId));
      const mainMeta = this.metadata(main);
      const giftMeta = this.metadata(gift);
      if (mainMeta?.deal !== this.dataset.kachingDealId || ![mainMeta?.bid, mainMeta?.bar].includes(this.dataset.kachingBaseBarId)) return null;
      if (giftMeta?.gift !== this.dataset.kachingGiftEntryId) return null;
      const amount = this.kachingBundle?.pricing?.()?.discountedPrice?.amount;
      if (amount !== undefined && Math.round(Number(amount) * 100) !== Number(state.price)) return null;
      return items;
    }
    groupId() { return window.crypto?.randomUUID?.() || `swv51-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; }
    formProperties(state) {
      const values = this.root?.swv5Purchase?.properties?.() || {};
      delete values.__kaching_bundles;
      return { ...values, _safewalk_package: this.packageName(state), _safewalk_expected_total_cents: String(state.price),
        _safewalk_addons: [state.includesBungee && 'bungee', state.includesCollar && 'collar'].filter(Boolean).join(',') || 'none' };
    }
    giftPersonalization() {
      const values = ['dog_name', 'phone'].map((role) => this.form?.querySelector(`[data-fourpaw-personalization-role="${role}"] input`)?.value?.trim()).filter(Boolean);
      return values.join(' · ') || this.dataset.giftPersonalizationEmpty;
    }
    payloadItems(items, state, groupId) {
      const mainProperties = this.formProperties(state);
      return items.map((item) => {
        const main = Number(item.id) === Number(state.main.id);
        const gift = Number(item.id) === Number(this.dataset.giftVariantId);
        const properties = { ...(item.properties || {}), '_Package ID': groupId };
        if (typeof properties.__kaching_bundles === 'object') properties.__kaching_bundles = JSON.stringify(properties.__kaching_bundles);
        if (main) Object.assign(properties, mainProperties);
        if (gift) properties[this.dataset.giftPersonalizationLabel] = this.giftPersonalization();
        return { id: Number(item.id), quantity: 1, ...(item.parent_id ? { parent_id: Number(item.parent_id) } : {}), properties };
      });
    }
    sectionIds() {
      const ids = new Set(['cart-drawer', 'cart-icon-bubble']);
      document.querySelector('cart-drawer')?.getSectionsToRender?.().forEach((section) => ids.add(section.id));
      return [...ids].filter(Boolean).slice(0, 5);
    }
    async post(url, payload) {
      const response = await fetch(url, { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const body = await response.json().catch(() => null);
      if (!response.ok || !body) throw new Error(`cart-${response.status || 'failed'}`);
      return body;
    }
    validateCart(cart, state, groupId) {
      const group = cart.items.filter((item) => item.properties?.['_Package ID'] === groupId);
      const expected = [state.main.id, ...(state.includesBungee ? [state.bungee.id] : []), ...(state.includesCollar ? [state.collar.id] : []), Number(this.dataset.giftVariantId)];
      if (group.length !== expected.length || expected.some((id) => !group.some((item) => Number(item.id) === Number(id)))) return false;
      const prices = new Map(group.map((item) => [Number(item.id), Number(item.final_price)]));
      return prices.get(Number(state.main.id)) === Number(state.main.price)
        && (!state.includesBungee || prices.get(Number(state.bungee.id)) === Number(this.dataset.bungeePrice))
        && (!state.includesCollar || prices.get(Number(state.collar.id)) === Number(this.dataset.collarPrice))
        && prices.get(Number(this.dataset.giftVariantId)) === 0;
    }
    renderCart(body) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.renderContents && body?.sections) {
        drawer.classList.remove('is-empty');
        drawer.renderContents(body);
      }
    }
    async rollback(groupId) {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((response) => response.json());
      const updates = Object.fromEntries(cart.items.filter((item) => item.properties?.['_Package ID'] === groupId).map((item) => [item.key, 0]));
      if (!Object.keys(updates).length) return;
      const body = await this.post('/cart/update.js', { updates, sections: this.sectionIds(), sections_url: location.pathname });
      this.renderCart(body);
    }
    hideError() { if (this.error) this.error.hidden = true; }
    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
      this.error.focus();
    }
    async onSubmit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy) return;
      this.hideError();
      let groupId = '';
      try {
        const initial = this.selection();
        if (!this.explicitSize(initial)) {
          this.showError(this.dataset.sizeRequiredLabel);
          this.root?.querySelector('.swv5-option--size')?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
          return;
        }
        this.busy = true;
        this.root?.swv5Purchase?.setBusy?.(true);
        this.buttonLabel(this.dataset.discountLoadingLabel, true);
        const state = await this.waitForBridge();
        const items = state && this.validatedItems(state);
        if (!state || !items) throw new Error('kaching-addons-not-ready');
        groupId = this.groupId();
        const sections = this.sectionIds();
        const body = await this.post('/cart/add.js', { items: this.payloadItems(items, state, groupId), sections, sections_url: location.pathname });
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((response) => response.json());
        if (!this.validateCart(cart, state, groupId)) {
          await this.rollback(groupId);
          groupId = '';
          throw new Error('cart-price-mismatch');
        }
        this.renderCart(body);
        this.root?.swv5Purchase?.setSuccess?.(true);
        this.root?.swv5Purchase?.setStatus?.('added');
        this.root?.dispatchEvent(new CustomEvent('swv5:cart-add-result', { bubbles: true, detail: { ok: true, package: this.packageName(state), groupId } }));
        window.SafeWalkV8GiftPackLifecycle?.schedule?.();
      } catch (error) {
        if (groupId) await this.rollback(groupId).catch(() => {});
        this.root?.swv5Purchase?.setSuccess?.(false);
        this.showError(this.dataset.errorLabel);
        this.root?.dispatchEvent(new CustomEvent('swv5:cart-add-result', { bubbles: true, detail: { ok: false, error: error instanceof Error ? error.message : 'unknown' } }));
      } finally {
        this.busy = false;
        this.root?.swv5Purchase?.setBusy?.(false);
        this.update();
      }
    }
  }
  customElements.define('safe-walk-addons', SafeWalkAddons);
  document.addEventListener('shopify:block:select', (event) => { const root = event.target?.closest?.('safe-walk-addons'), kind = event.target?.dataset?.addonBenefitFor; if (kind) root?.openInfo(kind, null); else root?.update(); });
})();
